const ALLOWED_EMBED_HOSTS = new Set([
  'www.youtube.com',
  'youtube.com',
  'www.youtube-nocookie.com',
  'youtube-nocookie.com',
  'youtu.be',
  'player.vimeo.com',
  'vimeo.com',
  'screenpal.com',
  'www.screenpal.com',
  'go.screenpal.com',
  'app.screenpal.com',
  'screencast-o-matic.com',
  'www.screencast-o-matic.com',
]);

export function getSafeHttpsUrl(value) {
  if (!value || typeof value !== 'string') return null;
  const raw = value.trim();
  if (!raw) return null;
  try {
    const withScheme = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    const parsed = new URL(withScheme);
    if (parsed.protocol === 'http:') parsed.protocol = 'https:';
    if (parsed.protocol !== 'https:') return null;
    return parsed.toString();
  } catch {
    return null;
  }
}

function extractYouTubeId(rawUrl) {
  const safe = getSafeHttpsUrl(rawUrl);
  if (!safe) return null;
  try {
    const parsed = new URL(safe);
    const host = parsed.hostname.replace(/^www\./, '');
    const pathParts = parsed.pathname.split('/').filter(Boolean);

    if (host === 'youtu.be' && pathParts[0]) return pathParts[0].slice(0, 11);

    if (
      host === 'youtube.com' ||
      host === 'youtube-nocookie.com' ||
      host === 'm.youtube.com' ||
      host === 'music.youtube.com'
    ) {
      const qv = parsed.searchParams.get('v');
      if (qv) return qv.slice(0, 11);
      if (pathParts[0] === 'embed' && pathParts[1]) return pathParts[1].slice(0, 11);
      if (pathParts[0] === 'shorts' && pathParts[1]) return pathParts[1].slice(0, 11);
      if (pathParts[0] === 'live' && pathParts[1]) return pathParts[1].slice(0, 11);
      if (pathParts[0] === 'v' && pathParts[1]) return pathParts[1].slice(0, 11);
    }
  } catch {
    return null;
  }
  return null;
}

function extractVimeoId(rawUrl) {
  const safe = getSafeHttpsUrl(rawUrl);
  if (!safe) return null;
  try {
    const parsed = new URL(safe);
    const host = parsed.hostname.replace(/^www\./, '');
    if (host !== 'vimeo.com' && host !== 'player.vimeo.com') return null;
    const parts = parsed.pathname.split('/').filter(Boolean);
    for (let i = parts.length - 1; i >= 0; i -= 1) {
      if (/^\d+$/.test(parts[i])) return parts[i];
    }
  } catch {
    return null;
  }
  return null;
}

function extractScreenPalId(rawUrl) {
  const safe = getSafeHttpsUrl(rawUrl);
  if (!safe) return null;
  try {
    const parsed = new URL(safe);
    const host = parsed.hostname.replace(/^www\./, '');
    const pathParts = parsed.pathname.split('/').filter(Boolean);
    const isScreenPalHost =
      host === 'screenpal.com' ||
      host === 'go.screenpal.com' ||
      host === 'app.screenpal.com' ||
      host === 'screencast-o-matic.com';
    if (!isScreenPalHost) return null;

    if (
      (pathParts[0] === 'player' || pathParts[0] === 'watch' || pathParts[0] === 'embed') &&
      pathParts[1]
    ) {
      const id = pathParts[1].replace(/[^A-Za-z0-9_-]/g, '');
      return id || null;
    }
  } catch {
    return null;
  }
  return null;
}

/**
 * ScreenPal espone l'URL diretto al MP4 via redirect 302 su:
 *   https://go.screenpal.com/player/stream/{id}
 * → CloudFront signed URL al file MP4 (range requests + content-type video/mp4).
 *
 * Usare il file MP4 diretto in un <video> HTML5 nativo evita completamente
 * l'iframe cross-origin, che su iOS Safari/PWA causava un crash del renderer
 * al primo caricamento (hard reload back to Home).
 */
function buildScreenPalDirectVideoUrl(rawUrl) {
  const id = extractScreenPalId(rawUrl);
  if (!id) return null;
  return `https://go.screenpal.com/player/stream/${id}`;
}

function withAutoplay(url, enabled) {
  if (!url || enabled) return url;
  try {
    const u = new URL(url);
    u.searchParams.delete('autoplay');
    return u.toString();
  } catch {
    return url;
  }
}

/** @returns {{ embedUrl: string|null, sourceUrl: string|null, isDirectVideo: boolean }} */
export function resolveVideoPlayback(videoUrl, { autoplay = true } = {}) {
  const sourceUrl = getSafeHttpsUrl(videoUrl);
  if (!sourceUrl) {
    return { embedUrl: null, sourceUrl: null, isDirectVideo: false };
  }

  const ytId = extractYouTubeId(sourceUrl);
  if (ytId) {
    const base = `https://www.youtube-nocookie.com/embed/${ytId}?rel=0&modestbranding=1&controls=1&playsinline=1`;
    return {
      embedUrl: autoplay ? `${base}&autoplay=1` : base,
      sourceUrl,
      isDirectVideo: false,
    };
  }

  const vimeoId = extractVimeoId(sourceUrl);
  if (vimeoId) {
    const base = `https://player.vimeo.com/video/${vimeoId}${autoplay ? '?autoplay=1' : ''}`;
    return {
      embedUrl: withAutoplay(base, autoplay),
      sourceUrl,
      isDirectVideo: false,
    };
  }

  const screenPalDirect = buildScreenPalDirectVideoUrl(sourceUrl);
  if (screenPalDirect) {
    // <video> HTML5 nativo invece di iframe ScreenPal: evita il crash WebKit su iOS.
    return { embedUrl: null, sourceUrl: screenPalDirect, isDirectVideo: true };
  }

  if (/\.(mp4|webm|ogg)(\?.*)?$/i.test(sourceUrl)) {
    return { embedUrl: null, sourceUrl, isDirectVideo: true };
  }

  try {
    const parsed = new URL(sourceUrl);
    if (!ALLOWED_EMBED_HOSTS.has(parsed.hostname)) {
      return { embedUrl: null, sourceUrl, isDirectVideo: false };
    }
    if (parsed.pathname.includes('/embed/') || parsed.pathname.includes('/player/')) {
      return { embedUrl: sourceUrl, sourceUrl, isDirectVideo: false };
    }
  } catch {
    return { embedUrl: null, sourceUrl, isDirectVideo: false };
  }

  return { embedUrl: null, sourceUrl, isDirectVideo: false };
}
